/**
 * Portal Init Sagas
 * Handles portal initialization - fetches /api/users/me and distributes data
 */

import { call, put, takeLatest, delay, type SagaReturnType } from '@ufc/core'

import { initActions } from './init.slice'
import { initApi } from '../infrastructure/api'
import { isAdminInit, isAuthenticatedInit } from '../domain/types'
import { adminContextActions } from '@ufc/domains/admin-context'

// ============================================================================
// Constants
// ============================================================================

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1000

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract error message from various error types
 */
function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message
  }

  const axiosError = error as { response?: { data?: unknown }; message?: string }
  const data = axiosError?.response?.data

  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    const typed = data as { error?: unknown; message?: unknown }
    if (typeof typed.message === 'string') return typed.message
    if (typeof typed.error === 'string') return typed.error
  }

  return fallback
}

// ============================================================================
// Worker Sagas
// ============================================================================

/**
 * Initialize portal by calling /api/users/me
 * Handles all user types: guest, ecommerce, customer, admin
 * Distributes admin data to admin-context for tenants list
 */
function* initializeSaga() {
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // Delay before retry (skip first attempt)
      if (attempt > 0) {
        yield delay(RETRY_DELAY_MS * attempt)

        if (import.meta.env.DEV) {
          console.log(`[PortalInit] Retry attempt ${attempt}/${MAX_RETRIES}`)
        }
      }

      // Call /api/users/me
      const data: SagaReturnType<typeof initApi.initialize> = yield call(
        [initApi, initApi.initialize]
      )

      // Store in portal-init state
      yield put(initActions.initializeSuccess(data))

      // === DISTRIBUTE DATA TO OTHER DOMAINS ===
      const roleType = isAuthenticatedInit(data) ? data.profile?.role?.type : null
      const isSalesOfficeUser = roleType === 'sales_office_user'

      if (isAdminInit(data)) {
        // Admin user: Set admin context and trigger tenant list fetch
        yield put(adminContextActions.setIsAdmin(true))

        // Fetch tenants list via admin-context saga (was previously bundled in /me response)
        yield put(adminContextActions.fetchTenantsList())

        // Restore previously selected tenant from localStorage
        yield put(adminContextActions.restoreFromStorage())

        if (import.meta.env.DEV) {
          console.log('[PortalInit] Admin init successful:', {
            type: data.type,
            permissions: data.permissions,
          })
        }
      } else if (isSalesOfficeUser) {
        // Sales office user should be treated as admin in portal UI,
        // but without global tenant switching context.
        yield put(adminContextActions.setIsAdmin(true))
        yield put(adminContextActions.setTenantsList([]))
        yield put(adminContextActions.clearTenantSelection())

        if (import.meta.env.DEV) {
          console.log('[PortalInit] Sales office admin context enabled')
        }
      } else {
        // Non-admin user: Clear admin context
        yield put(adminContextActions.setIsAdmin(false))
        yield put(adminContextActions.setTenantsList([]))
        yield put(adminContextActions.clearTenantSelection())

        if (import.meta.env.DEV) {
          console.log('[PortalInit] Init successful:', {
            type: data.type,
            hasProfile: 'profile' in data,
          })
        }
      }

      return // Success, exit saga
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))

      if (import.meta.env.DEV) {
        console.error(`[PortalInit] Attempt ${attempt + 1} failed:`, lastError.message)
      }
    }
  }

  // All retries exhausted
  const message = getErrorMessage(lastError, 'Failed to initialize portal')
  yield put(initActions.initializeFailure(message))
}

/**
 * Handle reset - clear all related state
 */
function* resetSaga() {
  yield put(adminContextActions.resetAdminContext())
}

// ============================================================================
// Watcher Saga
// ============================================================================

export function* initSaga() {
  yield takeLatest(initActions.initialize.type, initializeSaga)
  yield takeLatest(initActions.reset.type, resetSaga)
}
